import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { sanitizeSlug, resolveSlugCollision, generateSlugHash } from "@/utils/slug";
import { generateCopyName, normalizeTemplateName } from "@/utils/templateName";
import { toast } from "sonner";
import { useFeatureEnabled } from "@/components/FeatureGate";
import { createClient, SupabaseClient } from "@supabase/supabase-js";
import UserbackWidget from "@/components/UserbackWidget";
import TemplateForkDialog from "@/components/TemplateForkDialog";
import { isPreviewHost as checkPreviewHost, SNAP_BASE, TEMPLATES_BASE_URL, isTrustedOrigin } from "@/config/domains";
import { sanitizeDuplicateData } from "@/utils/sanitizeDuplicateData";
import { notifyEmbrfyrTemplateUpdated } from "@/lib/embrfyrTemplateSync";
import { isRuntimeVerbose } from "@/utils/runtimeNoisePolicy";
import { hasHydratableTemplateContent } from "@/editor/utils/templateDocumentPersistence";
import { ACTIVE_TEMPLATE_STORAGE_KEY } from "@/editor/utils/editorSession";
import { buildAuthContextFingerprint } from "@/editor/hooks/useParentContextMessageHandlers";
import { resolveTemplateRecordByReference, UUID_RE } from "@/utils/templateReferenceResolver";

import { getPdfTemplatePreset } from "@/shared/pdf/templates";
import { Loader2 } from "lucide-react";
import { cancelIdleTask, scheduleIdleTask } from "@/utils/browserIdle";

/** If the first animation frames after tab return are delayed, main thread is still busy — show feedback. */
const RESUME_STALL_RAF1_MS = 52;
const RESUME_STALL_RAF2_MS = 88;
const RESUME_OVERLAY_MIN_MS = 340;
const RESUME_OVERLAY_MAX_MS = 10000;
const IFRAME_HEALTHCHECK_TIMEOUT_MS = 1500;
const IFRAME_HEALTHCHECK_THROTTLE_MS = 1000;

function extractEditorRouteTemplateId(pathname: string): string | null {
  const match = /^\/editor\/([^/?#]+)/.exec(pathname);
  const raw = match?.[1];
  if (!raw) return null;
  try {
    return decodeURIComponent(raw).trim() || null;
  } catch {
    return raw.trim() || null;
  }
}

interface LicenseData {
  id: string;
  // Support both shapes from functions: { code } and older { license_key }
  code?: string;
  license_key?: string;
  activated_at: string;
  expires_at?: string;
  status: string;
  plan?: string | null;
  total_monthly_credits?: number | null;
}

const LICENSE_CHECK_TIMEOUT_ERROR = 'SNAPLAYER_LICENSE_CHECK_TIMEOUT';
const LICENSE_CHECK_TIMEOUT_MS = 8000;

const logEditorDebug = (...args: unknown[]) => {
  if (isRuntimeVerbose()) {
    console.log(...args);
  }
};

export default function Editor() {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const iframeOriginRef = useRef<string | null>(null);
  const iframeReadyRef = useRef<boolean>(false);
  const newTemplateSentRef = useRef<boolean>(false);
  const authSentRef = useRef<number>(0);
  const lastAuthContextPostFingerprintRef = useRef<string | null>(null);
  const lastCheckedUserIdRef = useRef<string | null>(null);
  const lastStartupDispatchKeyRef = useRef<string | null>(null);
  const location = useLocation();
  const { state } = location as { state?: { template?: any, source?: 'seed' | 'db' } };
  const isEditorRoute =
    location.pathname === '/editor' || location.pathname.startsWith('/editor/');
  const routeTemplateId = useMemo(() => extractEditorRouteTemplateId(location.pathname), [location.pathname]);
  const queryTemplateId = useMemo(() => {
    const value = new URLSearchParams(location.search).get('template');
    return value && value.trim().length ? value.trim() : null;
  }, [location.search]);
  const templateId = routeTemplateId ?? queryTemplateId;
  const navigate = useNavigate();
  const { user, session, loading: authLoading } = useAuth();
  const [licenseLoading, setLicenseLoading] = useState(true);
  const [hasValidLicense, setHasValidLicense] = useState(false);
  const [licenseError, setLicenseError] = useState<string | null>(null);
  const [profile, setProfile] = useState<{ username?: string | null; role?: string | null } | null>(null);
  const [licensePlan, setLicensePlan] = useState<string | null>(null);
  const [iframeReady, setIframeReady] = useState(false);
  const snaplinksEnabled = useFeatureEnabled('SNAPLINKS_ENABLED');
  const snaplinksEnabledRef = useRef(snaplinksEnabled);
  useEffect(() => { snaplinksEnabledRef.current = snaplinksEnabled; }, [snaplinksEnabled]);
  const [forkDialogOpen, setForkDialogOpen] = useState(false);
  const [pendingForkTemplate, setPendingForkTemplate] = useState<any>(null);
  const [pendingForkWin, setPendingForkWin] = useState<Window | null>(null);
  const [loadedTemplate, setLoadedTemplate] = useState<any>(null);
  const [templateLoadError, setTemplateLoadError] = useState<string | null>(null);
  const [resumePrepOverlay, setResumePrepOverlay] = useState(false);
  const [iframeCacheToken, setIframeCacheToken] = useState<string | null>(null);
  const editorTabWasHiddenRef = useRef(false);
  const resumeOverlayMinTimerRef = useRef<number | null>(null);
  const resumeOverlayIdleRef = useRef<number | null>(null);
  const resumeOverlayMaxTimerRef = useRef<number | null>(null);
  const iframeRecoveryOverlayTimerRef = useRef<number | null>(null);
  const iframeHealthCheckTimeoutRef = useRef<number | null>(null);
  const iframeHealthCheckTokenRef = useRef<string | null>(null);
  const lastIframeHealthCheckAtRef = useRef(0);
  const lastEditorPongAtRef = useRef(0);

  // Fetch template from database when templateId is in URL
  useEffect(() => {
    if (!isEditorRoute) return;
    if (!templateId || state?.template) {
      setLoadedTemplate(null);
      setTemplateLoadError(null);
      return;
    }

    let cancelled = false;
    setLoadedTemplate(null);
    setTemplateLoadError(null);

    const fetchTemplate = async () => {
      try {
        logEditorDebug('[Editor] Fetching template from URL param:', templateId);
        const { template, error, resolvedBy } = await resolveTemplateRecordByReference(supabase, templateId);

        if (cancelled) {
          return;
        }

        if (error || !template) {
          console.error('[Editor] Failed to fetch template:', { templateId, error, resolvedBy });
          setTemplateLoadError('Template not found');
          return;
        }

        logEditorDebug('[Editor] Template loaded from database:', {
          requestedReference: templateId,
          resolvedBy,
          templateId: template.id,
          slug: template.slug,
        });
        setLoadedTemplate(template);
      } catch (err) {
        if (cancelled) {
          return;
        }
        console.error('[Editor] Error fetching template:', err);
        setTemplateLoadError('Failed to load template');
      }
    };

    fetchTemplate();

    return () => {
      cancelled = true;
    };
  }, [isEditorRoute, templateId, state?.template]);

  useEffect(() => {
    const clearResumeTimers = () => {
      if (resumeOverlayMinTimerRef.current != null) {
        window.clearTimeout(resumeOverlayMinTimerRef.current);
        resumeOverlayMinTimerRef.current = null;
      }
      cancelIdleTask(resumeOverlayIdleRef.current);
      resumeOverlayIdleRef.current = null;
      if (resumeOverlayMaxTimerRef.current != null) {
        window.clearTimeout(resumeOverlayMaxTimerRef.current);
        resumeOverlayMaxTimerRef.current = null;
      }
    };

    const finishResumeOverlay = () => {
      clearResumeTimers();
      setResumePrepOverlay(false);
    };

    const scheduleResumeOverlayDismiss = () => {
      clearResumeTimers();
      resumeOverlayMinTimerRef.current = window.setTimeout(() => {
        resumeOverlayMinTimerRef.current = null;
        resumeOverlayIdleRef.current = scheduleIdleTask(() => {
          resumeOverlayIdleRef.current = null;
          setResumePrepOverlay(false);
        }, { timeout: 2400 });
      }, RESUME_OVERLAY_MIN_MS);

      resumeOverlayMaxTimerRef.current = window.setTimeout(() => {
        finishResumeOverlay();
      }, RESUME_OVERLAY_MAX_MS);
    };

    const tryDetectSlowReturnAfterTab = () => {
      if (!isEditorRoute) {
        return;
      }
      if (!editorTabWasHiddenRef.current) {
        return;
      }
      editorTabWasHiddenRef.current = false;

      const t0 = performance.now();
      requestAnimationFrame(() => {
        const lag1 = performance.now() - t0;
        if (lag1 >= RESUME_STALL_RAF1_MS) {
          setResumePrepOverlay(true);
          scheduleResumeOverlayDismiss();
          return;
        }
        requestAnimationFrame(() => {
          const lag2 = performance.now() - t0;
          if (lag2 >= RESUME_STALL_RAF2_MS) {
            setResumePrepOverlay(true);
            scheduleResumeOverlayDismiss();
          }
        });
      });
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        editorTabWasHiddenRef.current = true;
        return;
      }
      if (!isEditorRoute) {
        editorTabWasHiddenRef.current = false;
        return;
      }
      tryDetectSlowReturnAfterTab();
    };

    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted || !isEditorRoute) {
        return;
      }
      editorTabWasHiddenRef.current = true;
      tryDetectSlowReturnAfterTab();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pageshow', onPageShow as EventListener);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pageshow', onPageShow as EventListener);
      clearResumeTimers();
    };
  }, [isEditorRoute]);

  const legacy = new URLSearchParams(window.location.search).get('legacy') === '1';
  const reactQuery = new URLSearchParams(window.location.search).get('react');
  const shouldForceReact = reactQuery !== '0';
  const iframeSrcBase = legacy ? "/editor-app/index.legacy.html" : "/src/editor/index.html";
  const iframeSrc = iframeCacheToken ? `${iframeSrcBase}?v=${iframeCacheToken}` : iframeSrcBase;
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
  const supabaseServiceRoleKey = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY as string | undefined;
  // Preview mode support (Vercel deploy previews, branch deploys, and dev/beta subdomains)
  const isPreviewHost = typeof window !== 'undefined' && checkPreviewHost();
  const previewUserId = (import.meta.env as any).VITE_PREVIEW_USER_ID as string | undefined;
  const previewLicensePlan = (import.meta.env as any).VITE_PREVIEW_LICENSE_PLAN as string | undefined;
  const supabaseClientRef = useRef<SupabaseClient | null>(null);
  const supabaseClientKeyRef = useRef<string>('');
  const templatesBaseUrl = TEMPLATES_BASE_URL;
  const readStoredTemplateId = useCallback((): string | null => {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      const activeTemplateId = window.localStorage.getItem(ACTIVE_TEMPLATE_STORAGE_KEY);
      if (typeof activeTemplateId === 'string' && activeTemplateId.trim().length > 0) {
        return activeTemplateId.trim();
      }
    } catch (error) {
      console.warn('[Editor] Failed to read active template id from localStorage', error);
    }

    try {
      const raw = window.localStorage.getItem('snaplayer.editor.current-template.v1');
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      return typeof parsed?.id === 'string' && parsed.id.trim().length > 0
        ? parsed.id.trim()
        : null;
    } catch (error) {
      console.warn('[Editor] Failed to read current template metadata from localStorage', error);
      return null;
    }
  }, []);

  const generateTemplateId = useCallback(() => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    return `tpl-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }, []);

  const clearIframeRecoveryOverlayTimer = useCallback(() => {
    if (iframeRecoveryOverlayTimerRef.current != null) {
      window.clearTimeout(iframeRecoveryOverlayTimerRef.current);
      iframeRecoveryOverlayTimerRef.current = null;
    }
  }, []);

  const dismissResumeOverlay = useCallback(() => {
    clearIframeRecoveryOverlayTimer();
    setResumePrepOverlay(false);
  }, [clearIframeRecoveryOverlayTimer]);

  const clearPendingIframeHealthCheck = useCallback(() => {
    if (iframeHealthCheckTimeoutRef.current != null) {
      window.clearTimeout(iframeHealthCheckTimeoutRef.current);
      iframeHealthCheckTimeoutRef.current = null;
    }
    iframeHealthCheckTokenRef.current = null;
  }, []);

  const remountIframe = useCallback((reason: string) => {
    console.warn('[Editor] Remounting iframe after failed health check', { reason });
    clearPendingIframeHealthCheck();
    clearIframeRecoveryOverlayTimer();
    iframeReadyRef.current = false;
    setIframeReady(false);
    newTemplateSentRef.current = false;
    lastStartupDispatchKeyRef.current = null;
    authSentRef.current = 0;
    lastAuthContextPostFingerprintRef.current = null;
    iframeOriginRef.current = null;
    setResumePrepOverlay(true);
    iframeRecoveryOverlayTimerRef.current = window.setTimeout(() => {
      iframeRecoveryOverlayTimerRef.current = null;
      setResumePrepOverlay(false);
    }, RESUME_OVERLAY_MAX_MS);
    setIframeCacheToken(`${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`);
  }, [clearIframeRecoveryOverlayTimer, clearPendingIframeHealthCheck]);

  const postToIframe = useCallback(
    (targetWindow: Window | null | undefined, message: { type: string; payload?: unknown;[key: string]: unknown }) => {
      if (!targetWindow) return;
      const targetOrigin =
        iframeOriginRef.current && iframeOriginRef.current !== 'null'
          ? iframeOriginRef.current
          : '*';
      try {
        targetWindow.postMessage(message, targetOrigin);
      } catch (error) {
        console.warn('[Editor] postMessage to iframe failed, retrying with wildcard', { targetOrigin, error });
        if (targetOrigin !== '*') {
          targetWindow.postMessage(message, '*');
        }
      }
    },
    [],
  );

  const probeIframeHealth = useCallback((reason: string) => {
    if (!isEditorRoute || !iframeReadyRef.current) {
      return;
    }
    const now = Date.now();
    if (now - lastIframeHealthCheckAtRef.current < IFRAME_HEALTHCHECK_THROTTLE_MS) {
      return;
    }
    lastIframeHealthCheckAtRef.current = now;

    const targetWindow = iframeRef.current?.contentWindow;
    if (!targetWindow) {
      remountIframe(`${reason}:missing-window`);
      return;
    }

    const checkId = `${reason}-${now}`;
    clearPendingIframeHealthCheck();
    iframeHealthCheckTokenRef.current = checkId;
    postToIframe(targetWindow, {
      type: 'editorPing',
      payload: { checkId, reason },
      timestamp: now,
    });

    iframeHealthCheckTimeoutRef.current = window.setTimeout(() => {
      if (iframeHealthCheckTokenRef.current !== checkId) {
        return;
      }
      if (Date.now() - lastEditorPongAtRef.current <= IFRAME_HEALTHCHECK_TIMEOUT_MS) {
        clearPendingIframeHealthCheck();
        return;
      }
      remountIframe(`${reason}:timeout`);
    }, IFRAME_HEALTHCHECK_TIMEOUT_MS);
  }, [clearPendingIframeHealthCheck, isEditorRoute, postToIframe, remountIframe]);

  useEffect(() => {
    try {
      iframeOriginRef.current = new URL(iframeSrc, window.location.href).origin;
    } catch (error) {
      iframeOriginRef.current = null;
      console.warn('Unable to resolve iframe origin from src', { iframeSrc, error });
    }
  }, [iframeSrc]);

  const getAuthorizedSupabase = useCallback((): SupabaseClient => {
    if (!supabaseUrl) {
      throw new Error('SUPABASE_URL is not configured');
    }

    const clientKey = import.meta.env.DEV && supabaseServiceRoleKey
      ? 'service'
      : session?.access_token
        ? `session:${session.access_token}`
        : 'anon';

    if (supabaseClientRef.current && supabaseClientKeyRef.current === clientKey) {
      return supabaseClientRef.current;
    }

    let client: SupabaseClient;

    if (import.meta.env.DEV && supabaseServiceRoleKey) {
      client = createClient(supabaseUrl, supabaseServiceRoleKey, {
        auth: {
          persistSession: false,
          detectSessionInUrl: false,
          autoRefreshToken: false,
          storageKey: 'sl-editor-service-auth-ephemeral',
        },
      });
    } else if (session?.access_token) {
      client = createClient(supabaseUrl, supabaseAnonKey, {
        global: {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        },
        auth: {
          persistSession: false,
          detectSessionInUrl: false,
          autoRefreshToken: false,
          storageKey: 'sl-editor-session-auth-ephemeral',
        },
      });
    } else {
      supabaseClientRef.current = supabase;
      supabaseClientKeyRef.current = clientKey;
      return supabase;
    }

    supabaseClientRef.current = client;
    supabaseClientKeyRef.current = clientKey;
    return client;
  }, [session?.access_token, supabaseAnonKey, supabaseServiceRoleKey, supabaseUrl]);

  const sendAuthContext = useCallback(
    (targetWindow?: Window | null, explicitOrigin?: string) => {
      const win = targetWindow ?? iframeRef.current?.contentWindow;
      if (!win) return;
      if (explicitOrigin && explicitOrigin !== 'null') {
        iframeOriginRef.current = explicitOrigin;
      }

      const normalizedPlan = licensePlan && typeof licensePlan === 'string'
        ? licensePlan
        : (isPreviewHost ? (previewLicensePlan ?? 'plus') : null);
      const snapLinkEnabled = !normalizedPlan || normalizedPlan.toLowerCase() !== 'solo';

      // SECURITY: the service-role key bypasses all RLS. Only ever expose it in a
      // LOCAL dev build — `import.meta.env.DEV` is statically false in production
      // bundles, so Vite dead-code-eliminates this branch and the key can never be
      // inlined into a shipped bundle. The previous `isPreviewHost` branch ran in
      // PRODUCTION builds on preview domains and would have shipped the key to any
      // visitor if VITE_SUPABASE_SERVICE_ROLE_KEY were ever set on a preview env.
      // Removed. Preview/testing should use a seeded test user + anon key + RLS.
      const resolvedServiceKey = (import.meta.env.DEV && supabaseServiceRoleKey)
        ? supabaseServiceRoleKey
        : null;

      const authContext = {
        userId: user?.id ?? (isPreviewHost && previewUserId ? previewUserId : null),
        isDev: import.meta.env.DEV || isPreviewHost,
        supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
        anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        serviceRoleKey: resolvedServiceKey,
        accessToken: session?.access_token || null,
        username:
          profile?.username ??
          (user?.user_metadata && typeof user.user_metadata === 'object'
            ? (user.user_metadata as Record<string, unknown>)?.username
            : null),
        role: profile?.role ?? null,
        licensePlan: normalizedPlan,
        snapLinkEnabled,
      };

      const nextFingerprint = buildAuthContextFingerprint(authContext as Record<string, unknown>);
      if (lastAuthContextPostFingerprintRef.current === nextFingerprint) {
        return;
      }

      try {
        if (resolvedServiceKey) {
          (win as any).SUPABASE_SERVICE_ROLE_KEY = resolvedServiceKey;
        }
        if (authContext.anonKey) {
          (win as any).SUPABASE_ANON_KEY = authContext.anonKey;
        }
        if (authContext.supabaseUrl) {
          (win as any).SUPABASE_URL = authContext.supabaseUrl;
        }
        if (authContext.accessToken) {
          (win as any).SNAP_AUTH_TOKEN = authContext.accessToken;
        }
        if (authContext.userId) {
          (win as any).currentUserId = authContext.userId;
        }
        if (authContext.role) {
          (win as any).currentUserRole = authContext.role;
        }
      } catch (error) {
        console.warn('Unable to prime iframe auth globals', error);
      }

      postToIframe(win, {
        type: 'authContext',
        payload: authContext,
        timestamp: Date.now(),
      });
      lastAuthContextPostFingerprintRef.current = nextFingerprint;
    },
    [isPreviewHost, licensePlan, postToIframe, previewUserId, profile?.username, session?.access_token, supabaseServiceRoleKey, user?.id, user?.user_metadata],
  );

  const createTemplateFork = useCallback(async (template: any, win: Window) => {
    if (!user) return;

    const supabaseClient = getAuthorizedSupabase();

    toast.info('Creating a personal copy of this template...');
    const copyId = generateTemplateId();
    const nowIso = new Date().toISOString();
    const originalData =
      template.data && typeof template.data === 'object' && !Array.isArray(template.data)
        ? { ...(template.data as Record<string, unknown>) }
        : {};

    const originalCopyMeta =
      originalData &&
        typeof (originalData as Record<string, unknown>).copy_metadata === 'object' &&
        (originalData as Record<string, unknown>).copy_metadata
        ? ({
          ...(originalData as Record<string, unknown>).copy_metadata as Record<string, unknown>,
        })
        : {};

    const copyMetadata = {
      ...originalCopyMeta,
      source_template_id: template.id,
      source_owner_id: template.owner_id,
      source_visibility: template.visibility,
      copied_at: nowIso,
    };

    const copyData =
      Object.keys(originalData).length > 0
        ? sanitizeDuplicateData({ ...originalData, copy_metadata: copyMetadata })
        : { copy_metadata: copyMetadata };

    const copyName = generateCopyName(template.name ?? 'Template');

    const originalDescription =
      typeof template.description === 'string' ? template.description.trim() : '';
    const copyDescriptionParts = [
      originalDescription,
      `Copy of "${template.name ?? 'template'}" - customize as needed.`,
    ].filter((part) => part.length > 0);
    const copyDescription = copyDescriptionParts.join(' ').trim();

    try {
      const { data: createdTemplate, error: insertError } = await supabaseClient
        .from('templates')
        .insert({
          id: copyId,
          owner_id: user.id,
          name: copyName,
          description: copyDescription || null,
          cover_url: template.cover_url ?? null,
          visibility: 'private',
          data: copyData,
          slug: null,
          status: 'draft',
          activated_at: null,
        })
        .select('*')
        .single();

      if (insertError) {
        throw insertError;
      }

      // Copy images from source template to new template
      try {
        logEditorDebug('[Editor] Calling fork-template-images...', { sourceTemplateId: template.id, targetTemplateId: copyId });
        const { data: forkData, error: imageCopyError } = await supabaseClient.functions.invoke('fork-template-images', {
          body: {
            sourceTemplateId: template.id,
            targetTemplateId: copyId,
          },
        });

        if (imageCopyError) {
          console.warn('[Editor] Failed to copy template images:', imageCopyError);
        } else {
          logEditorDebug('[Editor] fork-template-images completed:', forkData);
        }

        // Re-fetch the template to get updated layers with new image paths
        const { data: updatedTemplate } = await supabaseClient
          .from('templates')
          .select('*')
          .eq('id', copyId)
          .single();

        return updatedTemplate || createdTemplate;
      } catch (imageCopyError) {
        console.warn('[Editor] Image copy process failed:', imageCopyError);
        return createdTemplate;
      }
    } catch (error) {
      console.error('[Editor] Failed to fork template:', error);
      throw error;
    }
  }, [user, getAuthorizedSupabase, generateTemplateId]);

  const handleForkSave = useCallback(async () => {
    if (!pendingForkTemplate || !pendingForkWin) return;

    setForkDialogOpen(false);

    try {
      const createdTemplate = await createTemplateFork(pendingForkTemplate, pendingForkWin);

      const assetHostFromData = (createdTemplate?.data as any)?.asset_host;
      const normalizedAssetHost = typeof assetHostFromData === 'string' && assetHostFromData.trim().length > 0
        ? assetHostFromData.trim().replace(/\/+$/, '')
        : (typeof createdTemplate?.asset_host === 'string' ? createdTemplate.asset_host?.trim().replace(/\/+$/, '') : null);

      const baseData =
        createdTemplate?.data && typeof createdTemplate.data === 'object'
          ? { ...(createdTemplate.data as Record<string, unknown>) }
          : {};
      const storageMeta =
        baseData && typeof baseData.storage === 'object' && baseData.storage
          ? (baseData.storage as Record<string, unknown>)
          : null;

      let templateData: Record<string, unknown> | null = null;
      const storageUrl =
        storageMeta && typeof storageMeta.url === 'string' && storageMeta.url.trim().length
          ? (storageMeta.url as string)
          : storageMeta && typeof storageMeta.key === 'string' && storageMeta.key.trim().length && templatesBaseUrl
            ? `${templatesBaseUrl}/${storageMeta.key}`
            : null;

      if (storageUrl) {
        try {
          const response = await fetch(storageUrl);
          if (response.ok) {
            templateData = await response.json();
          }
        } catch (fetchError) {
          console.warn('Template JSON fetch error', fetchError);
        }
      }

      if (!templateData && Object.keys(baseData).length) {
        templateData = { ...baseData };
      }

      // Strict hydration guard for fork path: if storage existed but we fell
      // back to baseData without layers, warn but continue (fork already
      // copied the template row; we send what we have).
      const hasStorageMetaFork = storageMeta && (typeof storageMeta.url === 'string' || typeof storageMeta.key === 'string');
      if (hasStorageMetaFork && templateData && !hasHydratableTemplateContent(templateData)) {
        console.warn('[Editor] Fork hydration incomplete: storage existed but no hydratable content in fallback data', {
          templateId: createdTemplate?.id,
        });
      }

      if (templateData) {
        if (normalizedAssetHost) {
          templateData.asset_host = normalizedAssetHost;
        }
        if (
          (!templateData.default_vars || typeof templateData.default_vars !== 'object') &&
          templateData.variables &&
          typeof templateData.variables === 'object' &&
          !Array.isArray(templateData.variables)
        ) {
          templateData.default_vars = { ...(templateData.variables as Record<string, unknown>) };
        }
      }

      const normalizedTemplate = {
        ...createdTemplate,
        asset_host: normalizedAssetHost,
        data: {
          ...baseData,
          asset_host: normalizedAssetHost,
          storage: storageMeta ?? baseData.storage,
        },
      };

      postToIframe(pendingForkWin, {
        type: 'loadTemplateResult',
        payload: {
          ok: true,
          template: normalizedTemplate,
          templateData: templateData ?? null,
          copyCreated: true,
          sourceTemplateId: pendingForkTemplate.id,
        },
      });
      toast.success('Personal copy created and loaded.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to copy template';
      postToIframe(pendingForkWin, { type: 'loadTemplateResult', payload: { ok: false, error: message } });
      toast.error(message);
    } finally {
      setPendingForkTemplate(null);
      setPendingForkWin(null);
    }
  }, [pendingForkTemplate, pendingForkWin, createTemplateFork, postToIframe, templatesBaseUrl]);

  const handleForkEvaluate = useCallback(async () => {
    if (!pendingForkTemplate || !pendingForkWin) return;

    setForkDialogOpen(false);

    // Load template without creating a copy
    const template = pendingForkTemplate;
    const win = pendingForkWin;

    const assetHostFromData = (template?.data as any)?.asset_host;
    const normalizedAssetHost = typeof assetHostFromData === 'string' && assetHostFromData.trim().length > 0
      ? assetHostFromData.trim().replace(/\/+$/, '')
      : (typeof template?.asset_host === 'string' ? template.asset_host?.trim().replace(/\/+$/, '') : null);

    const baseData =
      template?.data && typeof template.data === 'object'
        ? { ...(template.data as Record<string, unknown>) }
        : {};
    const storageMeta =
      baseData && typeof baseData.storage === 'object' && baseData.storage
        ? (baseData.storage as Record<string, unknown>)
        : null;

    let templateData: Record<string, unknown> | null = null;
    const storageUrl =
      storageMeta && typeof storageMeta.url === 'string' && storageMeta.url.trim().length
        ? (storageMeta.url as string)
        : storageMeta && typeof storageMeta.key === 'string' && storageMeta.key.trim().length && templatesBaseUrl
          ? `${templatesBaseUrl}/${storageMeta.key}`
          : null;

    if (storageUrl) {
      try {
        const response = await fetch(storageUrl);
        if (response.ok) {
          templateData = await response.json();
        }
      } catch (fetchError) {
        console.warn('Template JSON fetch error', fetchError);
      }
    }

    if (!templateData && Object.keys(baseData).length) {
      templateData = { ...baseData };
    }

    if (templateData) {
      if (normalizedAssetHost) {
        templateData.asset_host = normalizedAssetHost;
      }
      if (
        (!templateData.default_vars || typeof templateData.default_vars !== 'object') &&
        templateData.variables &&
        typeof templateData.variables === 'object' &&
        !Array.isArray(templateData.variables)
      ) {
        templateData.default_vars = { ...(templateData.variables as Record<string, unknown>) };
      }
    }

    const normalizedTemplate = {
      ...template,
      asset_host: normalizedAssetHost,
      data: {
        ...baseData,
        asset_host: normalizedAssetHost,
        storage: storageMeta ?? baseData.storage,
      },
    };

    postToIframe(win, {
      type: 'loadTemplateResult',
      payload: {
        ok: true,
        template: normalizedTemplate,
        templateData: templateData ?? null,
        copyCreated: false,
        sourceTemplateId: null,
      },
    });
    toast.success('Template loaded (read-only)');

    setPendingForkTemplate(null);
    setPendingForkWin(null);
  }, [pendingForkTemplate, pendingForkWin, postToIframe, templatesBaseUrl]);

  const handleForkCancel = useCallback(() => {
    if (pendingForkWin) {
      postToIframe(pendingForkWin, {
        type: 'loadTemplateResult',
        payload: { ok: false, error: 'Cancelled by user' },
      });
    }
    setForkDialogOpen(false);
    setPendingForkTemplate(null);
    setPendingForkWin(null);
  }, [pendingForkWin, postToIframe]);

  // Debug logging (once)
  useEffect(() => {
    logEditorDebug('Editor.tsx debug:', {
      search: window.location.search,
      legacy,
      reactQuery,
      shouldForceReact,
      iframeSrc
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check license when user is authenticated
  useEffect(() => {
    if (authLoading) return;

    // Development bypass - skip auth check entirely
    if (import.meta.env.DEV) {
      logEditorDebug('Development mode: bypassing authentication');
      setHasValidLicense(true);
      setLicenseLoading(false);
      setLicensePlan(null);
      return;
    }

    // Deploy preview support: treat Vercel preview as authenticated with preview user
    if (isPreviewHost) {
      logEditorDebug('Preview deployment detected: bypassing license fetch and using preview plan');
      setHasValidLicense(true);
      setLicenseLoading(false);
      setLicensePlan(previewLicensePlan ?? 'plus');
      return;
    }

    if (!user) {
      setLicenseLoading(false);
      setHasValidLicense(false);
      setLicensePlan(null);
      setLicenseError(null);
      lastCheckedUserIdRef.current = null;
      return;
    }

    // Clear license state when user changes to prevent stale data from previous user
    const hasUserChanged = lastCheckedUserIdRef.current !== null && lastCheckedUserIdRef.current !== user.id;
    if (hasUserChanged) {
      logEditorDebug('[Editor] User changed, clearing license state and re-checking');
      setHasValidLicense(false);
      setLicensePlan(null);
      setLicenseError(null);
    }

    const checkLicense = async () => {
      let timeoutHandle: number | null = null;
      try {
        setLicenseLoading(true);
        setLicenseError(null);

        const invokeResult = await Promise.race([
          supabase.functions.invoke('licenses'),
          new Promise<never>((_, reject) => {
            timeoutHandle = window.setTimeout(() => {
              reject(new Error(LICENSE_CHECK_TIMEOUT_ERROR));
            }, LICENSE_CHECK_TIMEOUT_MS);
          }),
        ]);

        if (timeoutHandle !== null) {
          window.clearTimeout(timeoutHandle);
          timeoutHandle = null;
        }

        const { data, error } = invokeResult;

        if (error) {
          console.error('License check failed:', error);
          setLicenseError('Failed to check license status');
          setHasValidLicense(false);
          return;
        }

        // Guard: Only update state if user hasn't changed during the async call
        if (lastCheckedUserIdRef.current !== null && lastCheckedUserIdRef.current !== user.id) {
          logEditorDebug('[Editor] User changed during license check, ignoring stale response');
          return;
        }

        const licenses: LicenseData[] = data?.rows || [];

        // Check if user has any valid license
        const now = new Date();
        const validLicense = licenses.find(license => {
          // Check if license is active/valid and not expired
          const status = (license.status ?? '').toLowerCase();
          const isActive = status === 'active' || status === 'activated' || status === 'valid';
          const isNotExpired = !license.expires_at || new Date(license.expires_at) > now;
          return isActive && isNotExpired;
        });

        setHasValidLicense(!!validLicense);
        setLicensePlan(validLicense?.plan ?? null);

        if (!validLicense && licenses.length > 0) {
          // Check if licenses exist but none are valid
          const hasInvalidStatus = licenses.some(l => {
            const status = (l.status ?? '').toLowerCase();
            return status !== 'active' && status !== 'activated' && status !== 'valid';
          });
          if (hasInvalidStatus) {
            setLicenseError('Your licenses are not in an active state. Please contact support.');
          } else {
            setLicenseError('Your license has expired. Please contact support.');
          }
        } else if (!validLicense) {
          setLicenseError('No valid license found. Please purchase a license to access the editor.');
        }

        // Track that we've checked this user
        lastCheckedUserIdRef.current = user.id;

      } catch (error) {
        if (timeoutHandle !== null) {
          window.clearTimeout(timeoutHandle);
          timeoutHandle = null;
        }
        if (error instanceof Error && error.message === LICENSE_CHECK_TIMEOUT_ERROR) {
          console.warn('[Editor] License check timed out; continuing in degraded mode for this session');
          setLicenseError(null);
          setHasValidLicense(true);
          setLicensePlan((previous) => previous ?? null);
          lastCheckedUserIdRef.current = user.id;
          return;
        }
        console.error('License check error:', error);
        setLicenseError('Failed to verify license');
        setHasValidLicense(false);
        setLicensePlan(null);
        // Still track the user ID even on error to prevent infinite retries
        lastCheckedUserIdRef.current = user.id;
      } finally {
        setLicenseLoading(false);
      }
    };

    checkLicense();
  }, [authLoading, session?.access_token, user?.id, isPreviewHost, previewLicensePlan]); // Include authLoading to re-run when auth state resolves

  useEffect(() => {
    if (!iframeReady) return;
    if (licenseLoading) return;
    if (!iframeRef.current?.contentWindow) return;
    sendAuthContext(iframeRef.current.contentWindow);
  }, [iframeReady, licenseLoading, licensePlan, sendAuthContext]);

  useEffect(() => {
    if (shouldForceReact && typeof window !== 'undefined') {
      window.SNAP_ENABLE_REACT_SNAPSHEET = true;
    }
  }, [shouldForceReact]);

  // Load template/new flow from the current editor navigation.
  useEffect(() => {
    if (!iframeReady || !isEditorRoute) return;

    const targetWindow = iframeRef.current?.contentWindow;
    if (!targetWindow) return;

    const consumeSessionValue = (key: string): string | null => {
      try {
        const value = sessionStorage.getItem(key);
        if (value) {
          sessionStorage.removeItem(key);
          return value;
        }
      } catch (_) {
        // ignore storage failures
      }
      return null;
    };

    const consumeSessionFlag = (key: string): boolean => consumeSessionValue(key) !== null;

    const params = new URLSearchParams(location.search);
    let templateId = routeTemplateId ?? queryTemplateId;
    let shouldOpenNew = params.get('new') === '1' || params.get('autoNew') === '1';
    let shouldOpenCanvaImport = params.get('canvaImport') === '1';
    let shouldOpenAiPanel = params.get('openAi') === '1';

    if (templateId) {
      consumeSessionValue('snap_pending_template_id');
    } else {
      templateId = consumeSessionValue('snap_pending_template_id');
    }

    if (!shouldOpenCanvaImport) {
      shouldOpenCanvaImport = consumeSessionFlag('snap_auto_canva_import');
    }

    if (!shouldOpenAiPanel) {
      shouldOpenAiPanel = consumeSessionFlag('snap_open_ai_panel');
    }

    if (!shouldOpenNew) {
      shouldOpenNew = consumeSessionFlag('snap_auto_new_template');
    }

    if (!templateId && !shouldOpenNew && !shouldOpenCanvaImport && !newTemplateSentRef.current) {
      templateId = readStoredTemplateId();
    }

    let dispatched = false;

    if (state?.template) {
      const stateTemplateId =
        typeof state.template?.id === 'string' && state.template.id.trim().length
          ? state.template.id.trim()
          : null;
      const dispatchKey = stateTemplateId
        ? `state-template:${stateTemplateId}`
        : `state-template:${location.key}`;

      if (lastStartupDispatchKeyRef.current !== dispatchKey) {
        if (stateTemplateId) {
          postToIframe(targetWindow, {
            type: 'autoLoadTemplate',
            payload: { templateId: stateTemplateId },
          });
        } else {
          postToIframe(targetWindow, {
            type: 'loadTemplateResult',
            payload: {
              ok: true,
              template: state.template,
              templateData:
                ((state.template as any)?.full_data as Record<string, unknown> | undefined) ??
                ((state.template as any)?.data as Record<string, unknown> | undefined) ??
                null,
            },
          });
        }
        lastStartupDispatchKeyRef.current = dispatchKey;
        dispatched = true;
      }
    } else if (templateId) {
      const dispatchKey = `template:${templateId}`;
      if (lastStartupDispatchKeyRef.current !== dispatchKey || !newTemplateSentRef.current) {
        logEditorDebug('Auto-loading template from editor navigation:', templateId);
        postToIframe(targetWindow, {
          type: 'autoLoadTemplate',
          payload: { templateId },
        });
        lastStartupDispatchKeyRef.current = dispatchKey;
        dispatched = true;
      }
    }

    if (shouldOpenCanvaImport) {
      const dispatchCanvaImport = () => {
        logEditorDebug('Opening Canva import from URL/session flag');
        postToIframe(targetWindow, {
          type: 'canvaImport'
        });
      };
      if (templateId) {
        window.setTimeout(dispatchCanvaImport, 150);
      } else {
        dispatchCanvaImport();
      }
      dispatched = true;
    } else if (shouldOpenNew) {
      logEditorDebug('Opening new template modal from URL');
      postToIframe(targetWindow, {
        type: 'newTemplate'
      });
      lastStartupDispatchKeyRef.current = `new-template:${location.key}`;
      dispatched = true;
    }

    if (shouldOpenAiPanel) {
      const dispatchOpenAiPanel = () => {
        logEditorDebug('Opening AI panel from URL/session flag');
        postToIframe(targetWindow, {
          type: 'openAiPanel'
        });
      };
      if (templateId) {
        window.setTimeout(dispatchOpenAiPanel, 180);
      } else {
        dispatchOpenAiPanel();
      }
      dispatched = true;
    }

    if (dispatched) {
      newTemplateSentRef.current = true;
    }
  }, [
    iframeReady,
    isEditorRoute,
    location.key,
    location.search,
    postToIframe,
    queryTemplateId,
    readStoredTemplateId,
    routeTemplateId,
    state?.template,
  ]);

  useEffect(() => {
    let cancelled = false;

    const loadProfile = async () => {
      if (!user) {
        if (!cancelled) {
          setProfile(null);
        }
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('username, role')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!cancelled) {
          if (error) {
            console.error('Failed to load profile for editor', error);
            setProfile(null);
          } else {
            setProfile(data);
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Unexpected profile load error', error);
          setProfile(null);
        }
      }
    };

    loadProfile();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    const onLoad = () => {
      const iframeWindow = iframeRef.current?.contentWindow;
      if (!iframeWindow) return;

      const urlParams = new URLSearchParams(location.search);
      let templateId = routeTemplateId ?? queryTemplateId;
      let shouldOpenNew = urlParams.get('new') === '1' || urlParams.get('autoNew') === '1';

      try {
        if (templateId) {
          sessionStorage.removeItem('snap_pending_template_id');
        } else {
          const storedTemplate = sessionStorage.getItem('snap_pending_template_id');
          if (storedTemplate) {
            templateId = storedTemplate;
            sessionStorage.removeItem('snap_pending_template_id');
          }
        }
      } catch (_) {
        // ignore storage failures
      }

      if (!templateId && !shouldOpenNew) {
        templateId = readStoredTemplateId();
      }

      if (!shouldOpenNew) {
        try {
          const storedFlag = sessionStorage.getItem('snap_auto_new_template');
          if (storedFlag) {
            shouldOpenNew = true;
            sessionStorage.removeItem('snap_auto_new_template');
          }
        } catch (_) {
          // ignore storage failures
        }
      }

      if (state?.template) {
        const stateTemplateId =
          typeof state.template?.id === 'string' && state.template.id.trim().length
            ? state.template.id.trim()
            : null;

        if (stateTemplateId) {
          iframeWindow.postMessage({
            type: 'autoLoadTemplate',
            payload: { templateId: stateTemplateId },
          }, window.location.origin);
        } else {
          iframeWindow.postMessage({
            type: 'loadTemplateResult',
            payload: {
              ok: true,
              template: state.template,
              templateData:
                ((state.template as any)?.full_data as Record<string, unknown> | undefined) ??
                ((state.template as any)?.data as Record<string, unknown> | undefined) ??
                null,
            },
          }, window.location.origin);
        }
        newTemplateSentRef.current = true;
        lastStartupDispatchKeyRef.current = stateTemplateId
          ? `state-template:${stateTemplateId}`
          : `state-template:${location.key}`;
      } else if (templateId) {
        // Fallback: tell iframe to auto-load by ID/reference.
        iframeWindow.postMessage({
          type: 'autoLoadTemplate',
          payload: { templateId },
        }, window.location.origin);
        newTemplateSentRef.current = true;
        lastStartupDispatchKeyRef.current = `template:${templateId}`;
      } else if (loadedTemplate) {
        const loadedTemplateId =
          typeof loadedTemplate?.id === 'string' && loadedTemplate.id.trim().length
            ? loadedTemplate.id.trim()
            : null;

        if (loadedTemplateId) {
          console.log('[Editor] Sending loadedTemplate id to iframe:', loadedTemplateId);
          iframeWindow.postMessage({
            type: 'autoLoadTemplate',
            payload: { templateId: loadedTemplateId },
          }, window.location.origin);
        } else {
          console.log('[Editor] Sending loadedTemplate payload to iframe');
          iframeWindow.postMessage({
            type: 'loadTemplateResult',
            payload: {
              ok: true,
              template: loadedTemplate,
              templateData:
                ((loadedTemplate as any)?.full_data as Record<string, unknown> | undefined) ??
                ((loadedTemplate as any)?.data as Record<string, unknown> | undefined) ??
                null,
            },
          }, window.location.origin);
        }
        newTemplateSentRef.current = true;
        lastStartupDispatchKeyRef.current = loadedTemplateId
          ? `template:${loadedTemplateId}`
          : `loaded-template:${location.key}`;
      } else if (!newTemplateSentRef.current && shouldOpenNew) {
        iframeWindow.postMessage({
          type: 'newTemplate',
        }, window.location.origin);
        newTemplateSentRef.current = true;
        lastStartupDispatchKeyRef.current = `new-template:${location.key}`;
      }
      if (!iframeReadyRef.current) {
        iframeReadyRef.current = true;
        setIframeReady(true);
      }
      dismissResumeOverlay();
      // Send auth context once shortly after load
      setTimeout(() => {
        if (authSentRef.current === 0) {
          sendAuthContext(iframeRef.current?.contentWindow ?? null);
          authSentRef.current = Date.now();
        }
      }, 300);
    };

    const iframe = iframeRef.current;
    iframe?.addEventListener('load', onLoad);
    return () => iframe?.removeEventListener('load', onLoad);
  }, [
    dismissResumeOverlay,
    loadedTemplate,
    location.key,
    location.search,
    queryTemplateId,
    readStoredTemplateId,
    routeTemplateId,
    sendAuthContext,
    state,
  ]); // Include loadedTemplate so we re-run when it loads

  // Handle async template loading - send canonical auto-load request when data becomes available
  useEffect(() => {
    if (!loadedTemplate || !iframeReady) return;
    // Skip if template was already sent by the onLoad handler
    if (newTemplateSentRef.current) return;

    const iframeWindow = iframeRef.current?.contentWindow;
    if (!iframeWindow) return;

    const loadedTemplateId =
      typeof loadedTemplate?.id === 'string' && loadedTemplate.id.trim().length
        ? loadedTemplate.id.trim()
        : null;

    if (loadedTemplateId) {
      console.log('[Editor] loadedTemplate became available, auto-loading id:', loadedTemplateId);
      iframeWindow.postMessage({
        type: 'autoLoadTemplate',
        payload: { templateId: loadedTemplateId },
      }, window.location.origin);
      newTemplateSentRef.current = true;
      lastStartupDispatchKeyRef.current = `template:${loadedTemplateId}`;
      return;
    }

    console.log('[Editor] loadedTemplate has no id, sending payload directly');
    iframeWindow.postMessage({
      type: 'loadTemplateResult',
      payload: {
        ok: true,
        template: loadedTemplate,
        templateData:
          ((loadedTemplate as any)?.full_data as Record<string, unknown> | undefined) ??
          ((loadedTemplate as any)?.data as Record<string, unknown> | undefined) ??
          null,
      },
    }, window.location.origin);
    newTemplateSentRef.current = true;
    lastStartupDispatchKeyRef.current = `loaded-template:${location.key}`;
  }, [iframeReady, loadedTemplate, location.key]);

  // Initial auth send only — further requests will be handled by onMessage('requestAuth')
  useEffect(() => {
    if (!iframeReady || authSentRef.current !== 0) return;
    sendAuthContext();
    authSentRef.current = Date.now();
  }, [iframeReady, sendAuthContext]);

  useEffect(() => {
    const onMessage = async (event: MessageEvent) => {
      // Debug: Log ALL messages to help diagnose
      if (event.data?.type === 'createSnapLink') {
        try {
          console.debug('[Editor] onMessage called for createSnapLink', {
            origin: event.origin,
            windowOrigin: window.location.origin,
            iframeOriginRef: iframeOriginRef.current,
            eventSource: event.source,
            hasIframeRef: !!iframeRef.current,
            dataType: event.data?.type
          });
        } catch { void 0; }
      }

      // Accept messages from iframe — use central origin trust check
      const isValidOrigin = (
        event.origin === window.location.origin ||
        (iframeOriginRef.current && iframeOriginRef.current !== 'null' && event.origin === iframeOriginRef.current) ||
        isTrustedOrigin(event.origin)
      );

      if (!isValidOrigin) {
        if (event.data?.type === 'createSnapLink') {
          console.error('[Editor] createSnapLink blocked by origin check', {
            origin: event.origin,
            windowOrigin: window.location.origin,
            iframeOriginRef: iframeOriginRef.current
          });
        } else {
          console.warn('Message from unauthorized origin:', event.origin);
        }
        return;
      }

      const { type, payload } = event.data || {};
      const win = iframeRef.current?.contentWindow;

      // Debug logging for createSnapLink messages
      if (type === 'createSnapLink') {
        try {
          console.debug('[Editor] Received createSnapLink message', {
            hasWin: Boolean(win),
            eventSource: event.source,
            iframeWin: win,
            origin: event.origin,
            requestId: payload?.requestId
          });
        } catch { void 0; }
      }

      if (!win) {
        if (type === 'createSnapLink') {
          console.error('[Editor] Cannot handle createSnapLink: iframe window not available', { requestId: payload?.requestId });
          // Try to send error response anyway (might work with wildcard origin)
          try {
            const errorWin = event.source as Window;
            if (errorWin && errorWin.postMessage) {
              errorWin.postMessage({
                type: 'createSnapLinkResult',
                payload: { ok: false, error: 'Editor iframe not ready. Please reload.', requestId: payload?.requestId, rowIndex: payload?.rowIndex },
              }, '*');
            }
          } catch { void 0; }
        }
        return;
      }
      // Gate SnapLink create/update when feature is disabled (main branch gating)
      if ((type === 'createSnapLink' || type === 'updateSnapLink') && !snaplinksEnabledRef.current) {
        try {
          const target = (event.source as Window) || win;
          if (target && target.postMessage) {
            target.postMessage({
              type: type === 'createSnapLink' ? 'createSnapLinkResult' : 'updateSnapLinkResult',
              payload: { ok: false, error: 'SnapLinks are disabled on this environment.', requestId: payload?.requestId, rowIndex: payload?.rowIndex },
            }, '*');
          }
        } catch { void 0; }
        return;
      }
      if (event.source === win && event.origin && event.origin !== 'null') {
        iframeOriginRef.current = event.origin;
      }

      try {
        if ((window as any).SNAP_DEBUG) {
          console.log('Received message:', { type, origin: event.origin });
        }
      } catch { void 0; }

      if (type === 'editorReady') {
        lastEditorPongAtRef.current = Date.now();
        clearPendingIframeHealthCheck();
        if (!iframeReadyRef.current) {
          iframeReadyRef.current = true;
          setIframeReady(true);
        }
        dismissResumeOverlay();
        postToIframe(win, { type: 'editorAck' });
        // Ensure the iframe receives auth context as soon as it signals readiness
        const now = Date.now();
        if (authSentRef.current === 0 || now - authSentRef.current > 2000) {
          try {
            sendAuthContext(win, event.origin);
            authSentRef.current = now;
          } catch (error) {
            console.error('Failed to send auth context on editorReady:', error);
          }
        }
        return;
      }

      if (type === 'editorPong') {
        lastEditorPongAtRef.current = Date.now();
        clearPendingIframeHealthCheck();
        dismissResumeOverlay();
        return;
      }

      if (type === 'navigate' && payload === 'home') {
        navigate('/dashboard');
        return;
      }

      if (type === 'requestUpload') {
        try {
          const supabaseClient = getAuthorizedSupabase();
          const body = {
            ...payload,
            user_id: payload?.user_id || user?.id || null,
          };
          const { data, error } = await supabaseClient.functions.invoke('storage-sign', { body });
          postToIframe(win, {
            type: 'uploadSigned',
            payload: { ok: !error, ...data, error: error?.message },
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to sign upload';
          console.error('requestUpload error:', message);
          postToIframe(win, {
            type: 'uploadSigned',
            payload: { ok: false, error: message },
          });
        }
      }

      // Handle auth context requests
      if (type === 'requestAuth') {
        try {
          if ((window as any).SNAP_DEBUG) {
            console.log('Sending auth context to iframe:', {
              targetOrigin: event.origin,
              userId: user?.id ?? null,
              hasServiceKey: !!(import.meta.env.DEV ? import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY : null),
            });
          }
        } catch { void 0; }

        try {
          const now = Date.now();
          // Avoid spamming auth context; send at most once every 2 seconds
          if (now - authSentRef.current > 2000) {
            sendAuthContext(win, event.origin);
            authSentRef.current = now;
          }
        } catch (error) {
          console.error('Failed to send auth context:', error);
        }
        return;
      }

      if (type === 'updateTemplatePreview') {
        const templateId = typeof payload?.templateId === 'string' ? payload.templateId : null;
        const coverUrl = typeof payload?.coverUrl === 'string' ? payload.coverUrl : null;

        if (!templateId || !coverUrl || !user) {
          postToIframe(win, {
            type: 'templatePreviewUpdated',
            payload: { ok: false, templateId, error: 'Missing template ID, preview URL, or user.' },
          });
          return;
        }

        try {
          const supabaseClient = getAuthorizedSupabase();
          const { error } = await supabaseClient
            .from('templates')
            .update({ cover_url: coverUrl, preview_url: coverUrl, thumbnail_url: coverUrl })
            .eq('id', templateId)
            .eq('owner_id', user.id);

          if (error) {
            throw error;
          }

          postToIframe(win, {
            type: 'templatePreviewUpdated',
            payload: { ok: true, templateId, coverUrl },
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to update template preview';
          postToIframe(win, {
            type: 'templatePreviewUpdated',
            payload: { ok: false, templateId, error: message },
          });
        }
        return;
      }

      if (type === 'saveTemplate') {
        if (!user) {
          postToIframe(win, { type: 'saveTemplateResult', payload: { ok: false, error: 'Not authenticated' } });
          return;
        }

        try {
          const supabaseClient = getAuthorizedSupabase();
          const requestId = typeof payload?.requestId === 'string' ? payload.requestId : undefined;
          const templateId = typeof payload?.id === 'string' ? payload.id : undefined;
          const ensuredTemplateId = templateId ?? generateTemplateId();
          const sanitizedAssetHost = typeof payload?.asset_host === 'string' && payload.asset_host.trim().length > 0
            ? payload.asset_host.trim().replace(/\/+$/, '')
            : null;

          const isRecord = (value: unknown): value is Record<string, unknown> =>
            !!value && typeof value === 'object' && !Array.isArray(value);

          const toRecord = (value: unknown): Record<string, unknown> => {
            if (!isRecord(value)) return {};
            return { ...(value as Record<string, unknown>) };
          };

          const normalizeLayerArray = (value: unknown): any[] => {
            if (!Array.isArray(value)) return [];
            return value.map((layer) => (isRecord(layer) ? { ...layer } : layer));
          };

          const normalizeVariationArray = (value: unknown): Array<Record<string, unknown>> => {
            if (!Array.isArray(value)) return [];
            return value.map((row) => (isRecord(row) ? { ...row } : {}));
          };

          const normalizeSnapRows = (value: unknown) => {
            if (!Array.isArray(value)) return [];
            return value.map((row) => {
              if (!isRecord(row)) {
                return { index: 0, data: {}, images: {} };
              }
              const rowRecord = row as Record<string, unknown>;
              return {
                index: Number.isFinite(Number(rowRecord.index)) ? Number(rowRecord.index) : 0,
                data: toRecord(rowRecord.data),
                images: toRecord(rowRecord.images),
              };
            });
          };

          const coerceDimension = (value: unknown, fallback: number) => {
            const numeric = Number(value);
            if (!Number.isFinite(numeric)) return fallback;
            return Math.max(32, Math.min(4096, Math.round(numeric)));
          };

          const dataPayload = isRecord(payload?.data) ? (payload.data as Record<string, unknown>) : null;

          const normalizedLayers = normalizeLayerArray(dataPayload?.layers ?? payload?.layers);
          const rawVariables = isRecord(dataPayload?.variables)
            ? dataPayload?.variables
            : isRecord(payload?.variables)
              ? (payload.variables as Record<string, unknown>)
              : {};
          const rawDefaultVars = isRecord(dataPayload?.default_vars)
            ? dataPayload?.default_vars
            : isRecord(payload?.default_vars)
              ? (payload.default_vars as Record<string, unknown>)
              : rawVariables;
          const variablesRecord = toRecord(rawVariables);
          const defaultVarsRecord = toRecord(rawDefaultVars);
          const variations = normalizeVariationArray(dataPayload?.variations ?? payload?.variations);
          const snapRows = normalizeSnapRows(dataPayload?.snap_rows ?? payload?.snap_rows);

          const canvasWidth = coerceDimension(dataPayload?.canvas_width ?? payload?.canvas_width, 1080);
          const canvasHeight = coerceDimension(dataPayload?.canvas_height ?? payload?.canvas_height, 1080);

          const resolvedStatus =
            (typeof dataPayload?.status === 'string' && dataPayload.status.trim().length > 0
              ? dataPayload.status.trim()
              : typeof payload?.status === 'string' && payload.status.trim().length > 0
                ? payload.status.trim()
                : 'draft');
          const normalizedStatus = resolvedStatus === 'active' ? 'active' : 'draft';
          const resolvedActivatedAt =
            typeof dataPayload?.activated_at === 'string' && dataPayload.activated_at.trim().length > 0
              ? dataPayload.activated_at.trim()
              : typeof payload?.activated_at === 'string' && payload.activated_at.trim().length > 0
                ? payload.activated_at.trim()
                : null;

          const assetHostCandidate =
            sanitizedAssetHost ??
            (typeof dataPayload?.asset_host === 'string' && dataPayload.asset_host.trim().length
              ? dataPayload.asset_host.trim()
              : typeof payload?.asset_host === 'string' && payload.asset_host.trim().length
                ? payload.asset_host.trim()
                : null);
          const normalizedAssetHost = assetHostCandidate ? assetHostCandidate.replace(/\/+$/, '') : null;

          const resolvedVariables = Object.keys(variablesRecord).length
            ? variablesRecord
            : { ...defaultVarsRecord };
          const resolvedDefaultVars = Object.keys(defaultVarsRecord).length
            ? defaultVarsRecord
            : { ...variablesRecord };

          const templateDataData: Record<string, unknown> = {
            layers: normalizedLayers,
            variables: resolvedVariables,
            default_vars: resolvedDefaultVars,
            variations,
            snap_rows: snapRows,
            canvas_width: canvasWidth,
            canvas_height: canvasHeight,
            status: resolvedStatus,
          };

          if (normalizedAssetHost) {
            templateDataData.asset_host = normalizedAssetHost;
          }

          if (!Object.keys(templateDataData.default_vars as Record<string, unknown>).length) {
            templateDataData.default_vars = {
              ...(templateDataData.variables as Record<string, unknown>),
            };
          }

          const nowIso = new Date().toISOString();
          templateDataData.updated_at = nowIso;
          templateDataData.revision = nowIso;

          const templateJson = JSON.stringify(templateDataData);
          const templateBytes =
            typeof TextEncoder !== 'undefined'
              ? new TextEncoder().encode(templateJson).length
              : templateJson.length;

          const { data: signed, error: signError } = await supabaseClient.functions.invoke('storage-sign', {
            body: {
              ext: 'json',
              mime: 'application/json',
              bytes: templateBytes,
              user_id: user.id,
              template_id: ensuredTemplateId,
              resource: 'template-json',
            },
          });

          if (signError) {
            throw new Error(signError.message ?? 'Failed to sign template upload');
          }

          const signedPayload = signed as Record<string, unknown> | null | undefined;
          const signedUrl = typeof signedPayload?.url === 'string' ? signedPayload.url : null;
          const signedKey = typeof signedPayload?.key === 'string' ? signedPayload.key : null;
          if (!signedUrl || !signedKey) {
            throw new Error('Failed to obtain upload URL for template data');
          }

          const uploadResponse = await fetch(signedUrl, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: templateJson,
          });

          if (!uploadResponse.ok) {
            throw new Error(`Template upload failed (${uploadResponse.status} ${uploadResponse.statusText})`);
          }

          const storageMeta = {
            provider: 'r2',
            bucket: typeof signedPayload?.bucket === 'string' ? signedPayload.bucket : 'snaplayer-templates',
            key: signedKey,
            url:
              (typeof signedPayload?.publicUrl === 'string' && signedPayload.publicUrl.trim().length
                ? signedPayload.publicUrl
                : templatesBaseUrl
                  ? `${templatesBaseUrl}/${signedKey}`
                  : null),
            version: nowIso,
            bytes: templateBytes,
            uploaded_at: nowIso,
          };

          const resolvedName =
            typeof payload?.name === 'string' && payload.name.trim().length
              ? payload.name.trim()
              : 'Untitled Template';
          const resolvedDescription =
            typeof payload?.description === 'string' ? payload.description : null;

          const templateDataMeta: Record<string, unknown> = {
            storage: storageMeta,
            variables: resolvedVariables,
            default_vars: resolvedDefaultVars,
            canvas_width: canvasWidth,
            canvas_height: canvasHeight,
            status: normalizedStatus,
            activated_at: resolvedActivatedAt,
            asset_host: normalizedAssetHost,
            revision: nowIso,
            updated_at: nowIso,
            layer_count: normalizedLayers.length,
            variation_count: variations.length,
            snap_row_count: snapRows.length,
          };

          if (variations.length) {
            templateDataMeta['variations_meta'] = variations.map((variation) => ({
              id: variation.id,
              name: variation.name,
              canvas_width: variation.canvas_width,
              canvas_height: variation.canvas_height,
            }));
          }

          const templateData = {
            id: ensuredTemplateId,
            owner_id: user.id,
            name: resolvedName,
            description: resolvedDescription,
            cover_url: payload?.thumbnail || null,
            visibility:
              payload?.visibility === 'community' || payload?.visibility === 'snaplayer'
                ? payload.visibility
                : 'private',
            data: templateDataMeta,
          };

          // Use templates-save function which handles forking automatically
          const { data: saveResult, error: saveError } = await supabaseClient.functions.invoke('templates-save', {
            body: {
              owner_id: user.id,
              template: {
                ...templateData,
                status: normalizedStatus,
                activated_at: resolvedActivatedAt,
              },
              template_meta: {
                updated_at: nowIso,
                revision: nowIso,
              },
              storage: storageMeta,
            },
          });

          if (saveError) {
            throw saveError;
          }

          const row = saveResult?.template;
          if (!row) {
            throw new Error('Template save returned no data');
          }

          notifyEmbrfyrTemplateUpdated(row.id ?? ensuredTemplateId, { source: 'editor-save' });

          const normalizedTemplate = row
            ? {
              ...row,
              asset_host: normalizedAssetHost,
              data: {
                ...(row.data ?? {}),
                asset_host: normalizedAssetHost,
                storage: {
                  ...(row.data as Record<string, unknown> | null | undefined)?.storage,
                  ...storageMeta,
                },
              },
            }
            : null;

          postToIframe(win, {
            type: 'saveTemplateResult',
            payload: { ok: true, template: normalizedTemplate, templateData: templateDataData, requestId },
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Unknown error';
          postToIframe(win, {
            type: 'saveTemplateResult',
            payload: { ok: false, error: message, requestId: typeof payload?.requestId === 'string' ? payload.requestId : undefined },
          });
        }
      }

      if (type === 'loadTemplate' || type === 'autoLoadTemplate') {
        try {
          const supabaseClient = getAuthorizedSupabase();
          const templateReference = payload?.templateId || payload?.id;
          if (!templateReference) {
            postToIframe(win, { type: 'loadTemplateResult', payload: { ok: false, error: 'No template ID provided' } });
            return;
          }

          // Handle preset slugs by synthesizing a PDF-mode template.
          if (!UUID_RE.test(templateReference)) {
            const preset = getPdfTemplatePreset(templateReference);
            if (preset) {
              const syntheticPages = preset.pages.map((p: any, i: number) => ({
                id: crypto.randomUUID(),
                sort_order: i,
                width: 1200,
                height: 2400,
                layers: [],
              }));
              postToIframe(win, {
                type: 'loadTemplateResult',
                payload: {
                  ok: true,
                  template: {
                    id: templateReference,
                    name: preset.title,
                    description: preset.description,
                    status: 'draft',
                    visibility: 'private',
                    data: {},
                  },
                  templateData: {
                    editor_mode: 'pdf',
                    pages: syntheticPages,
                    metadata: { source_preset_id: templateReference },
                  },
                },
              });
              toast.success('Template preset loaded');
              return;
            }
          }

          const { template, error: templateError } = await resolveTemplateRecordByReference(supabaseClient, templateReference);

          if (templateError) {
            postToIframe(win, { type: 'loadTemplateResult', payload: { ok: false, error: templateError } });
            toast.error(templateError === 'Template not found' ? templateError : `Failed to load template: ${templateError}`);
            return;
          }

          if (!template) {
            postToIframe(win, { type: 'loadTemplateResult', payload: { ok: false, error: 'Template not found' } });
            toast.error('Template not found');
            return;
          }

          const templateRecord = template;
          const createdCopy = false;

          if (template.owner_id !== user?.id) {
            if (!user) {
              const message = 'Sign in to customize this template.';
              postToIframe(win, { type: 'loadTemplateResult', payload: { ok: false, error: message } });
              toast.error(message);
              return;
            }

            const isCopyableVisibility = template.visibility === 'community' || template.visibility === 'snaplayer';

            if (!isCopyableVisibility) {
              const message = 'This template cannot be copied.';
              postToIframe(win, { type: 'loadTemplateResult', payload: { ok: false, error: message } });
              toast.error(message);
              return;
            }

            // Always show fork dialog for Snaplayer/community templates
            // so users can create a new copy each time
            setPendingForkTemplate(template);
            setPendingForkWin(win);
            setForkDialogOpen(true);
            return;
          }

          const assetHostFromData = (templateRecord?.data as any)?.asset_host;
          const normalizedAssetHost = typeof assetHostFromData === 'string' && assetHostFromData.trim().length > 0
            ? assetHostFromData.trim().replace(/\/+$/, '')
            : (typeof templateRecord?.asset_host === 'string' ? templateRecord.asset_host?.trim().replace(/\/+$/, '') : null);

          const baseData =
            templateRecord?.data && typeof templateRecord.data === 'object'
              ? { ...(templateRecord.data as Record<string, unknown>) }
              : {};
          const storageMeta =
            baseData && typeof baseData.storage === 'object' && baseData.storage
              ? (baseData.storage as Record<string, unknown>)
              : null;

          let templateData: Record<string, unknown> | null = null;
          const storageUrl =
            storageMeta && typeof storageMeta.url === 'string' && storageMeta.url.trim().length
              ? (storageMeta.url as string)
              : storageMeta && typeof storageMeta.key === 'string' && storageMeta.key.trim().length && templatesBaseUrl
                ? `${templatesBaseUrl}/${storageMeta.key}`
                : null;

          if (storageUrl) {
            try {
              const response = await fetch(storageUrl, { cache: 'no-store' });
              if (response.ok) {
                templateData = await response.json();
              } else {
                console.warn('Failed to fetch template JSON from R2', {
                  templateId: templateRecord.id,
                  status: response.status,
                  statusText: response.statusText,
                });
              }
            } catch (fetchError) {
              console.warn('Template JSON fetch error', fetchError);
            }
          }

          const hasStorageMeta = storageMeta && (typeof storageMeta.url === 'string' || typeof storageMeta.key === 'string');
          if (hasStorageMeta && !templateData) {
            console.warn('[Editor] Template hydration failed: storage metadata exists but remote JSON could not be loaded', {
              templateId: templateRecord.id,
              storageUrl: storageMeta?.url,
              storageKey: storageMeta?.key,
            });
            postToIframe(win, {
              type: 'loadTemplateResult',
              payload: { ok: false, error: 'Template data could not be loaded. Please try again.' },
            });
            toast.error('Template data unavailable. Please try again.');
            return;
          }

          if (!templateData && Object.keys(baseData).length) {
            templateData = { ...baseData };
          }

          // Strict hydration guard: if storage metadata existed but loaded data has no layers,
          // the template cannot be rendered properly.
          if (hasStorageMeta && templateData && !hasHydratableTemplateContent(templateData)) {
            console.warn('[Editor] Template hydration failed: storage existed but no hydratable content in fallback data', {
              templateId: templateRecord.id,
              storageUrl: storageMeta?.url,
              storageKey: storageMeta?.key,
              dataKeys: Object.keys(templateData),
            });
            postToIframe(win, {
              type: 'loadTemplateResult',
              payload: { ok: false, error: 'Template data could not be loaded. Please try again.' },
            });
            toast.error('Template data unavailable. Please try again.');
            return;
          }

          if (templateData) {
            if (normalizedAssetHost) {
              templateData.asset_host = normalizedAssetHost;
            }
            if (
              (!templateData.default_vars || typeof templateData.default_vars !== 'object') &&
              templateData.variables &&
              typeof templateData.variables === 'object' &&
              !Array.isArray(templateData.variables)
            ) {
              templateData.default_vars = { ...(templateData.variables as Record<string, unknown>) };
            }
          }

          const normalizedTemplate = {
            ...templateRecord,
            asset_host: normalizedAssetHost,
            data: {
              ...baseData,
              asset_host: normalizedAssetHost,
              storage: storageMeta ?? baseData.storage,
            },
          };

          postToIframe(win, {
            type: 'loadTemplateResult',
            payload: {
              ok: true,
              template: normalizedTemplate,
              templateData: templateData ?? null,
              copyCreated: createdCopy,
              sourceTemplateId: createdCopy ? template.id : null,
            },
          });
          toast.success(createdCopy ? 'Personal copy created and loaded.' : 'Template loaded');
        } catch (error) {
          postToIframe(win, {
            type: 'loadTemplateResult',
            payload: { ok: false, error: error instanceof Error ? error.message : 'Unknown error' },
          });
          toast.error('Failed to load template');
        }
      }

      if (type === 'fetchTemplates') {
        // TEMPORARILY DISABLED: Mark iframe as ready when it first requests templates
        // Since we disabled editorReady handshake, don't set iframeReady here
        // if (!iframeReady) {
        //   setIframeReady(true);
        // }

        try {
          const supabaseClient = getAuthorizedSupabase();
          const payloadVisibilities = Array.isArray(payload?.visibilities) ? payload.visibilities : [];
          const isVisibilityKey = (value: unknown): value is 'private' | 'snaplayer' | 'community' =>
            value === 'private' || value === 'snaplayer' || value === 'community';
          const requestedVisibilities = payloadVisibilities.filter(isVisibilityKey);

          const withSnapPageState = async (templates: any[], visibility: 'private' | 'snaplayer' | 'community') => {
            const rows = Array.isArray(templates) ? templates : [];
            const templateIds = Array.from(
              new Set(
                rows
                  .map((row) => (typeof row?.id === 'string' ? row.id : null))
                  .filter((value): value is string => Boolean(value)),
              ),
            );

            if (!templateIds.length) {
              return rows;
            }

            const { data: snapPageRows, error: snapPageError } = await supabaseClient
              .from('snappages')
              .select('template_id')
              .eq('enabled', true)
              .in('template_id', templateIds);

            if (snapPageError) {
              console.warn(`[Editor] Failed to fetch SnapPage state for ${visibility} templates`, snapPageError);
              return rows;
            }

            const enabledTemplateIds = new Set(
              (snapPageRows ?? [])
                .map((row) => (typeof row?.template_id === 'string' ? row.template_id : null))
                .filter((value): value is string => Boolean(value)),
            );

            return rows.map((row) => ({
              ...row,
              snap_page_enabled: typeof row?.id === 'string' && enabledTemplateIds.has(row.id),
            }));
          };

          const fetchTemplatesForVisibility = async (visibility: 'private' | 'snaplayer' | 'community') => {
            let query = supabaseClient
              .from('templates')
              .select('id, name, description, cover_url, created_at, updated_at, visibility, status, activated_at, folder_id, folder:template_folders(id, name, sort_order, owner_id, created_at, updated_at)')
              .order('created_at', { ascending: false });

            if (visibility === 'private') {
              if (user) {
                query = query.eq('owner_id', user.id).eq('visibility', 'private');
              } else {
                query = query.eq('visibility', 'private').eq('owner_id', '00000000-0000-0000-0000-000000000000');
              }
            } else {
              query = query.eq('visibility', visibility);
            }

            const { data, error } = await query;
            if (error) {
              console.error(`Error fetching ${visibility} templates:`, error);
              return [];
            }
            return withSnapPageState(data ?? [], visibility);
          };

          if (requestedVisibilities.length > 0) {
            const templatesByVisibility: Record<'private' | 'snaplayer' | 'community', any[]> = {
              private: [],
              snaplayer: [],
              community: [],
            };

            for (const visibility of requestedVisibilities) {
              templatesByVisibility[visibility] = await fetchTemplatesForVisibility(visibility);
            }

            postToIframe(win, {
              type: 'templatesFetched',
              payload: {
                templatesByVisibility,
                templates: templatesByVisibility.private ?? [],
              },
            });
            return;
          }

          const { visibility } = payload;
          if (!visibility || !isVisibilityKey(visibility)) {
            postToIframe(win, { type: 'templatesFetched', payload: { templates: [] } });
            return;
          }

          const templates = await fetchTemplatesForVisibility(visibility);
          postToIframe(win, {
            type: 'templatesFetched',
            payload: {
              templates,
              templatesByVisibility: {
                private: visibility === 'private' ? templates : [],
                snaplayer: visibility === 'snaplayer' ? templates : [],
                community: visibility === 'community' ? templates : [],
              },
            },
          });
        } catch (error) {
          console.error('Error in fetchTemplates:', error);
          postToIframe(win, { type: 'templatesFetched', payload: { templates: [] } });
        }
      }

      if (type === 'renameTemplate') {
        const requestId: string | undefined = typeof payload?.requestId === 'string' ? payload.requestId : undefined;
        const templateId: string | null = typeof payload?.templateId === 'string' ? payload.templateId : null;
        const rawName = typeof payload?.name === 'string' ? payload.name : '';
        const newName = normalizeTemplateName(rawName.trim());
        const description = typeof payload?.description === 'string' ? payload.description : undefined;

        if (!user) {
          postToIframe(win, {
            type: 'templateRenamed',
            payload: { ok: false, requestId, error: 'You must be signed in to rename templates.' },
          });
          return;
        }

        if (!templateId || !newName.length) {
          postToIframe(win, {
            type: 'templateRenamed',
            payload: { ok: false, requestId, error: 'Template ID and name are required.' },
          });
          return;
        }

        try {
          const supabaseClient = getAuthorizedSupabase();
          const updatePayload: Record<string, unknown> = { name: newName };
          if (description !== undefined) {
            updatePayload.description = description;
          }

          const { data, error } = await supabaseClient
            .from('templates')
            .update(updatePayload)
            .eq('id', templateId)
            .eq('owner_id', user.id)
            .select('id, name, description, cover_url, created_at, visibility')
            .single();

          if (error) {
            throw error;
          }

          postToIframe(win, {
            type: 'templateRenamed',
            payload: { ok: true, requestId, template: data },
          });
          toast.success('Template renamed');
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to rename template';
          console.error('Error renaming template:', error);
          postToIframe(win, {
            type: 'templateRenamed',
            payload: { ok: false, requestId, error: message },
          });
          toast.error(`Failed to rename template: ${message}`);
        }
      }

      if (type === 'duplicateTemplate') {
        const requestId: string | undefined = typeof payload?.requestId === 'string' ? payload.requestId : undefined;
        const templateId: string | null = typeof payload?.templateId === 'string' ? payload.templateId : null;
        const rawName = typeof payload?.name === 'string' ? payload.name : '';
        const newName = rawName.trim();

        if (!user) {
          postToIframe(win, {
            type: 'templateDuplicated',
            payload: { ok: false, requestId, error: 'You must be signed in to duplicate templates.', sourceTemplateId: templateId },
          });
          return;
        }

        if (!templateId || !newName.length) {
          postToIframe(win, {
            type: 'templateDuplicated',
            payload: { ok: false, requestId, error: 'Template ID and name are required.', sourceTemplateId: templateId },
          });
          return;
        }

        try {
          const supabaseClient = getAuthorizedSupabase();
          const { data: source, error: fetchError } = await supabaseClient
            .from('templates')
            .select('id, owner_id, name, description, visibility, status, activated_at, cover_url, data, created_at, updated_at')
            .eq('id', templateId)
            .eq('owner_id', user.id)
            .single();

          if (fetchError) {
            throw fetchError;
          }
          if (!source) {
            throw new Error('Template not found.');
          }

          const nowIso = new Date().toISOString();
          const insertPayload: Record<string, unknown> = {
            owner_id: user.id,
            name: newName,
            description: typeof source.description === 'string' ? source.description : '',
            visibility: 'private',
            status: 'draft',
            activated_at: null,
            cover_url: source.cover_url ?? null,
            data: sanitizeDuplicateData(source.data as Record<string, unknown> | null),
            created_at: nowIso,
            updated_at: nowIso,
          };

          const { data: inserted, error: insertError } = await supabaseClient
            .from('templates')
            .insert(insertPayload)
            .select('id, name, description, cover_url, preview_url, thumbnail_url, created_at, updated_at, visibility, status, activated_at')
            .single();

          if (insertError) {
            throw insertError;
          }

          postToIframe(win, {
            type: 'templateDuplicated',
            payload: { ok: true, requestId, template: inserted, sourceTemplateId: templateId },
          });
          toast.success('Template duplicated');
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to duplicate template';
          console.error('Error duplicating template:', error);
          postToIframe(win, {
            type: 'templateDuplicated',
            payload: { ok: false, requestId, error: message, sourceTemplateId: templateId },
          });
          toast.error(`Failed to duplicate template: ${message}`);
        }
      }

      if (type === 'deleteTemplate') {
        const requestId: string | undefined = typeof payload?.requestId === 'string' ? payload.requestId : undefined;
        const templateId: string | null = typeof payload?.templateId === 'string' ? payload.templateId : null;

        if (!user) {
          postToIframe(win, {
            type: 'templateDeleted',
            payload: { ok: false, requestId, templateId, error: 'You must be signed in to delete templates.' },
          });
          return;
        }

        if (!templateId) {
          postToIframe(win, {
            type: 'templateDeleted',
            payload: { ok: false, requestId, templateId, error: 'Template ID is required.' },
          });
          return;
        }

        try {
          const supabaseClient = getAuthorizedSupabase();
          const { error } = await supabaseClient
            .from('templates')
            .delete()
            .eq('id', templateId)
            .eq('owner_id', user.id);

          if (error) {
            throw error;
          }

          postToIframe(win, {
            type: 'templateDeleted',
            payload: { ok: true, requestId, templateId },
          });
          toast.success('Template deleted');
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to delete template';
          console.error('Error deleting template:', error);
          postToIframe(win, {
            type: 'templateDeleted',
            payload: { ok: false, requestId, templateId, error: message },
          });
          toast.error(`Failed to delete template: ${message}`);
        }
      }

      if (type === 'fetchSnapLinks') {
        try { console.debug('[Editor] fetchSnapLinks request', { templateId: payload?.templateId, requestId: payload?.requestId }); } catch { void 0; }
        try {
          const supabaseClient = getAuthorizedSupabase();
          const templateId = typeof payload?.templateId === 'string' ? payload.templateId : null;
          const requestId: string | undefined = typeof payload?.requestId === 'string' ? payload.requestId : undefined;
          if (!templateId || !user) {
            postToIframe(win, {
              type: 'snapLinksFetched',
              payload: { templateId, requestId, snaplinks: [] },
            });
            return;
          }

          const { data: snaplinksData, error: snaplinksError } = await supabaseClient
            .from('snaplinks')
            .select('id, slug, default_vars, created_at, updated_at, template_id, is_current')
            .eq('template_id', templateId)
            .order('updated_at', { ascending: false })
            .limit(500);

          if (snaplinksError) {
            throw snaplinksError;
          }

          const snaplinks = Array.isArray(snaplinksData) ? snaplinksData : [];
          try { console.debug('[Editor] fetchSnapLinks success', { templateId, count: snaplinks.length, requestId }); } catch { void 0; }
          postToIframe(win, {
            type: 'snapLinksFetched',
            payload: { templateId, requestId, snaplinks },
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to load SnapLinks';
          try { console.warn('[Editor] fetchSnapLinks error', { message }); } catch { void 0; }
          postToIframe(win, {
            type: 'snapLinksFetched',
            payload: { templateId: payload?.templateId ?? null, requestId: typeof payload?.requestId === 'string' ? payload.requestId : undefined, snaplinks: [], error: message },
          });
        }
      }

      // Ensure or update the primary SnapLink via Edge Function (enforces rate limit)
      if (type === 'ensureSnapLink') {
        const requestId: string | undefined = typeof payload?.requestId === 'string' ? payload.requestId : undefined;
        try {
          const supabaseClient = getAuthorizedSupabase();
          const templateId: string | undefined = payload?.templateId || payload?.template?.id;
          if (!templateId) {
            postToIframe(win, { type: 'ensureSnapLinkResult', payload: { ok: false, error: 'No template selected.', requestId } });
            return;
          }
          const requestedSlug: string | undefined = typeof payload?.slug === 'string' && payload.slug.trim().length
            ? sanitizeSlug(payload.slug, { fallback: payload.slug })
            : undefined;

          // Call ensure function with fallback to legacy local logic only if function fails entirely
          try {
            const { data, error } = await supabaseClient.functions.invoke('snaplinks-ensure', {
              body: { template_id: templateId, slug: requestedSlug, resolve_collisions: true },
            });
            if (error) throw new Error(error.message ?? 'Failed to ensure SnapLink');
            postToIframe(win, { type: 'ensureSnapLinkResult', payload: { ok: true, slug: data?.slug ?? requestedSlug ?? null, id: data?.record?.id ?? null, requestId } });
            return;
          } catch (ensureErr: any) {
            const message = ensureErr?.message ?? 'Failed to ensure SnapLink';

            const normalized = String(message).toLowerCase();
            const isRateLimited = normalized.includes('429')
              || normalized.includes('too many requests')
              || normalized.includes('rate limit');

            if (isRateLimited) {
              // Ensure is a best-effort call; if throttled, fall back to existing current slug.
              try {
                const { data: existingRows, error: existingError } = await supabaseClient
                  .from('snaplinks')
                  .select('id, slug, updated_at')
                  .eq('template_id', templateId)
                  .eq('is_current', true)
                  .order('updated_at', { ascending: false })
                  .limit(1);

                if (!existingError && Array.isArray(existingRows) && existingRows.length > 0) {
                  const existing = existingRows[0] as { id?: string | null; slug?: string | null };
                  const existingSlug = typeof existing?.slug === 'string'
                    ? sanitizeSlug(existing.slug, { fallback: '' })
                    : '';
                  if (existingSlug) {
                    postToIframe(win, {
                      type: 'ensureSnapLinkResult',
                      payload: {
                        ok: true,
                        slug: existingSlug,
                        id: typeof existing?.id === 'string' ? existing.id : null,
                        requestId,
                        throttled: true,
                      },
                    });
                    return;
                  }
                }
              } catch {
                // fall through to user-facing fallback error
              }

              postToIframe(win, {
                type: 'ensureSnapLinkResult',
                payload: {
                  ok: false,
                  error: 'SnapLink updates are temporarily rate-limited. Please try again in a moment.',
                  requestId,
                  rate_limited: true,
                },
              });
              return;
            }

            postToIframe(win, { type: 'ensureSnapLinkResult', payload: { ok: false, error: message, requestId } });
            return;
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to ensure SnapLink';
          postToIframe(win, {
            type: 'ensureSnapLinkResult',
            payload: { ok: false, error: message, requestId },
          });
        }
      }

      if (type === 'createSnapLink') {
        try {
          console.debug('[Editor] createSnapLink request', {
            requestId: payload?.requestId,
            templateId: payload?.templateId,
            rowIndex: payload?.rowIndex,
            scope: payload?.scope,
            slug: payload?.slug,
            hasUser: Boolean(user),
            userId: user?.id
          });
        } catch { void 0; }
        if (!user) {
          postToIframe(win, {
            type: 'createSnapLinkResult',
            payload: { ok: false, error: 'You must be signed in to create SnapURLs.', requestId: typeof payload?.requestId === 'string' ? payload.requestId : undefined, scope: typeof payload?.scope === 'string' ? payload.scope : undefined, rowIndex: typeof payload?.rowIndex === 'number' ? payload.rowIndex : undefined },
          });
          return;
        }

        const requestId: string | undefined = typeof payload?.requestId === 'string' ? payload.requestId : undefined;
        const scope: string | undefined = typeof payload?.scope === 'string' ? payload.scope : undefined;
        const rowIndex: number | undefined = typeof payload?.rowIndex === 'number' ? payload.rowIndex : undefined;

        try {
          const sanitizeVars = (raw: unknown): Record<string, string> => {
            if (!raw || typeof raw !== 'object') return {};
            const entries = Object.entries(raw as Record<string, unknown>)
              .filter(([key]) => typeof key === 'string' && key.length > 0)
              .map(([key, value]) => [key, value == null ? '' : String(value)] as const);
            return Object.fromEntries(entries);
          };

          const templateId: string | undefined = payload?.templateId || payload?.template?.id;
          if (!templateId) {
            postToIframe(win, {
              type: 'createSnapLinkResult',
              payload: { ok: false, error: 'No template selected. Load or save a template before generating SnapURLs.', requestId, scope, rowIndex },
            });
            return;
          }

          const requestedSlug: string = typeof payload?.slug === 'string' ? payload.slug : '';
          const defaultVars = sanitizeVars(payload?.defaultVars);
          const allowCollisionResolution = payload?.resolveCollisions !== false;

          const supabaseClient = getAuthorizedSupabase();

          // Prepare base slug (server will also de-dupe when resolve_collisions=true)
          let baseSlug = sanitizeSlug(requestedSlug, { fallback: '' });
          if (!baseSlug) {
            const hash = await generateSlugHash(templateId, defaultVars);
            baseSlug = `snap-${hash}`;
          }

          // Call edge function to create SnapURL (unified path)
          let data: any | null = null;
          let error: any | null = null;
          try {
            const resp = await supabaseClient.functions.invoke('snaplinks-save', {
              body: {
                template_id: templateId,
                slug: baseSlug,
                default_vars: defaultVars,
                resolve_collisions: allowCollisionResolution,
                make_current: payload?.makeCurrent === true,
                preserve_current: payload?.preserveCurrent === true,
                reuse_existing: payload?.reuseExisting !== false,
              },
            });
            data = resp.data; error = resp.error;
          } catch (e) {
            error = e;
          }
          if (error) {
            try { console.warn('[Editor] createSnapLink function error', { requestId, message: error.message }); } catch { void 0; }
            throw new Error(error.message ?? 'Failed to create SnapLink');
          }

          const resolvedSlug: string | undefined = data?.slug ?? data?.record?.slug ?? baseSlug;
          const overrideBase = (() => { try { return (typeof window !== 'undefined' ? window.localStorage.getItem('sl_snap_base_override') : null) || null; } catch { return null; } })();
          const base = (data?.url ? null : (overrideBase && /^https?:\/\//i.test(overrideBase) ? overrideBase : SNAP_BASE));
          const url: string | undefined = data?.url ?? (resolvedSlug ? `${String(base).replace(/\/+$/, '')}/${resolvedSlug}` : undefined);

          // Warm the renderer in background (best effort)
          try {
            if (typeof url === 'string' && url.length) {
              fetch(url, { cache: 'no-store', mode: 'no-cors' }).catch(() => undefined);
            }
          } catch { void 0; }

          try { console.debug('[Editor] createSnapLink success', { requestId, scope, rowIndex, slug: resolvedSlug, hasUrl: Boolean(url) }); } catch { void 0; }
          postToIframe(win, {
            type: 'createSnapLinkResult',
            payload: { ok: true, slug: resolvedSlug, url, record: data?.record ?? null, requestId, scope, rowIndex },
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to create SnapLink';
          try { console.warn('[Editor] createSnapLink failed', { requestId, message }); } catch { void 0; }
          postToIframe(win, {
            type: 'createSnapLinkResult',
            payload: { ok: false, error: message, requestId, scope, rowIndex },
          });
        }
      }

      if (type === 'updateSnapLink') {
        try {
          console.debug('[Editor] updateSnapLink request', {
            requestId: payload?.requestId,
            templateId: payload?.templateId,
            rowIndex: payload?.rowIndex,
            scope: payload?.scope,
            slug: payload?.slug,
            hasUser: Boolean(user),
            userId: user?.id,
          });
        } catch { void 0; }
        if (!user) {
          postToIframe(win, {
            type: 'updateSnapLinkResult',
            payload: { ok: false, error: 'You must be signed in to update SnapURLs.', requestId: typeof payload?.requestId === 'string' ? payload.requestId : undefined, scope: typeof payload?.scope === 'string' ? payload.scope : undefined, rowIndex: typeof payload?.rowIndex === 'number' ? payload.rowIndex : undefined },
          });
          return;
        }

        const requestId: string | undefined = typeof payload?.requestId === 'string' ? payload.requestId : undefined;
        const scope: string | undefined = typeof payload?.scope === 'string' ? payload.scope : undefined;
        const rowIndex: number | undefined = typeof payload?.rowIndex === 'number' ? payload.rowIndex : undefined;

        try {
          const sanitizeVars = (raw: unknown): Record<string, string> => {
            if (!raw || typeof raw !== 'object') return {};
            const entries = Object.entries(raw as Record<string, unknown>)
              .filter(([key]) => typeof key === 'string' && key.length > 0)
              .map(([key, value]) => [key, value == null ? '' : String(value)] as const);
            return Object.fromEntries(entries);
          };

          const templateId: string | undefined = payload?.templateId || payload?.template?.id;
          if (!templateId) {
            postToIframe(win, {
              type: 'updateSnapLinkResult',
              payload: { ok: false, error: 'No template selected. Load or save a template before updating SnapURLs.', requestId, scope, rowIndex },
            });
            return;
          }

          const requestedSlug = typeof payload?.slug === 'string'
            ? sanitizeSlug(payload.slug, { fallback: '' })
            : '';
          if (!requestedSlug) {
            postToIframe(win, {
              type: 'updateSnapLinkResult',
              payload: { ok: false, error: 'A valid slug is required to update this SnapLink.', requestId, scope, rowIndex },
            });
            return;
          }

          const defaultVars = sanitizeVars(payload?.defaultVars);
          const supabaseClient = getAuthorizedSupabase();
          const updatedAt = new Date().toISOString();

          const { data: updatedRecord, error: updateError } = await supabaseClient
            .from('snaplinks')
            .update({
              template_id: templateId,
              default_vars: defaultVars,
              updated_at: updatedAt,
            })
            .eq('template_id', templateId)
            .eq('slug', requestedSlug)
            .select('id, slug, template_id, default_vars, created_at, updated_at')
            .maybeSingle();

          if (updateError) {
            throw updateError;
          }

          let record = updatedRecord;
          let url: string | undefined;

          if (!record) {
            let data: any | null = null;
            let error: any | null = null;
            try {
              const resp = await supabaseClient.functions.invoke('snaplinks-save', {
                body: {
                  template_id: templateId,
                  slug: requestedSlug,
                  default_vars: defaultVars,
                  resolve_collisions: false,
                },
              });
              data = resp.data;
              error = resp.error;
            } catch (invokeError) {
              error = invokeError;
            }

            if (error) {
              throw new Error(error.message ?? 'Failed to update SnapLink');
            }

            record = data?.record ?? null;
            url = data?.url;
          }

          const resolvedSlug = typeof record?.slug === 'string' && record.slug.length > 0
            ? record.slug
            : requestedSlug;
          const overrideBase = (() => { try { return (typeof window !== 'undefined' ? window.localStorage.getItem('sl_snap_base_override') : null) || null; } catch { return null; } })();
          const base = (url ? null : (overrideBase && /^https?:\/\//i.test(overrideBase) ? overrideBase : SNAP_BASE));
          const resolvedUrl = url ?? (resolvedSlug ? `${String(base).replace(/\/+$/, '')}/${resolvedSlug}` : undefined);

          try {
            if (typeof resolvedUrl === 'string' && resolvedUrl.length) {
              fetch(resolvedUrl, { cache: 'no-store', mode: 'no-cors' }).catch(() => undefined);
            }
          } catch { void 0; }

          try { console.debug('[Editor] updateSnapLink success', { requestId, scope, rowIndex, slug: resolvedSlug, hasUrl: Boolean(resolvedUrl) }); } catch { void 0; }
          postToIframe(win, {
            type: 'updateSnapLinkResult',
            payload: { ok: true, slug: resolvedSlug, url: resolvedUrl, record, requestId, scope, rowIndex },
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to update SnapLink';
          try { console.warn('[Editor] updateSnapLink failed', { requestId, message }); } catch { void 0; }
          postToIframe(win, {
            type: 'updateSnapLinkResult',
            payload: { ok: false, error: message, requestId, scope, rowIndex },
          });
        }
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [clearPendingIframeHealthCheck, dismissResumeOverlay, generateTemplateId, getAuthorizedSupabase, navigate, postToIframe, sendAuthContext, session, supabaseServiceRoleKey, templatesBaseUrl, user]);

  useEffect(() => {
    if (!isEditorRoute) {
      clearPendingIframeHealthCheck();
      return;
    }

    let probeTimer: number | null = null;

    const scheduleProbe = (reason: string) => {
      if (document.visibilityState === 'hidden' || !iframeReadyRef.current) {
        return;
      }
      if (probeTimer != null) {
        window.clearTimeout(probeTimer);
      }
      probeTimer = window.setTimeout(() => {
        probeTimer = null;
        probeIframeHealth(reason);
      }, 120);
    };

    const onFocus = () => scheduleProbe('focus');
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        scheduleProbe('visibility');
      }
    };
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        scheduleProbe('pageshow');
      }
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pageshow', onPageShow as EventListener);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pageshow', onPageShow as EventListener);
      if (probeTimer != null) {
        window.clearTimeout(probeTimer);
      }
      clearPendingIframeHealthCheck();
    };
  }, [clearPendingIframeHealthCheck, isEditorRoute, probeIframeHealth]);

  useEffect(() => {
    return () => {
      clearPendingIframeHealthCheck();
      clearIframeRecoveryOverlayTimer();
    };
  }, [clearIframeRecoveryOverlayTimer, clearPendingIframeHealthCheck]);

  // Show loading state
  if (authLoading || licenseLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-sl-bg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sl-accent mx-auto mb-4"></div>
          <p className="text-sl-text">Loading editor...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return (
      <div className="h-screen flex items-center justify-center bg-sl-bg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sl-accent mx-auto mb-4"></div>
          <p className="text-sl-text">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  // Show license gate if no valid license
  if (!hasValidLicense) {
    return (
      <div className="h-screen flex items-center justify-center bg-sl-bg">
        <div className="max-w-md mx-auto text-center p-8">
          <div className="mb-6">
            <div className="w-16 h-16 bg-sl-accent/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fa-solid fa-lock text-sl-accent text-2xl"></i>
            </div>
            <h1 className="text-2xl font-bold text-sl-text mb-2">License Required</h1>
            <p className="text-sl-muted">
              {licenseError || 'You need a valid license to access the SnapLayer editor.'}
            </p>
          </div>

          <div className="space-y-4">
            <button
              onClick={() => navigate('/account/licenses')}
              className="w-full bg-sl-accent hover:bg-sl-accent/90 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              View Licenses
            </button>

            <button
              onClick={() => navigate('/')}
              className="w-full bg-sl-surface-2 hover:bg-sl-surface text-sl-text px-6 py-3 rounded-lg font-medium transition-colors"
            >
              Back to Dashboard
            </button>
          </div>

          <div className="mt-8 text-sm text-sl-muted">
            <p>Need help? <a href="/dashboard" className="text-sl-accent hover:underline">Open Dashboard Support</a></p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh / 0.85)' }}>
      <UserbackWidget />
      {/* <div className="bg-yellow-100 text-yellow-900 text-sm px-4 py-2 border-b border-yellow-200">
        <b>BETA:</b> Limits are not enforced. Export templates r...image dimensions and good compression before upload in order to converve resources and banwidth, as we have not implemented compression or resolution reduction in the beta version.
      </div> */}
      <main className="relative flex-1">
        <iframe key={iframeCacheToken ?? 'initial'} ref={iframeRef} title="Editor" src={iframeSrc} className="w-full h-full border-0" />
        {resumePrepOverlay ? (
          <div
            className="pointer-events-none absolute inset-0 z-[80] flex items-center justify-center bg-sl-bg/55 backdrop-blur-[1px]"
            aria-busy="true"
            aria-live="polite"
          >
            <div className="pointer-events-none flex flex-col items-center gap-3 rounded-xl border border-sl-surface-2 bg-sl-surface/95 px-6 py-5 shadow-lg">
              <Loader2 className="h-10 w-10 shrink-0 animate-spin text-sl-accent" aria-hidden />
              <p className="max-w-[16rem] text-center text-sm text-sl-text">Waking up editor…</p>
            </div>
          </div>
        ) : null}
      </main>
      <TemplateForkDialog
        open={forkDialogOpen}
        templateName={pendingForkTemplate?.name ?? ''}
        onSave={handleForkSave}
        onEvaluate={handleForkEvaluate}
        onCancel={handleForkCancel}
      />
    </div>
  );
}
